import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Check, X, Shuffle, Trash2 } from "lucide-react";
import { listFlashcards, genFlashcards, reviewFlashcard, deleteFlashcard } from "@/lib/api";
import { toast } from "sonner";

function FlashcardView({ card, flipped, onFlip }) {
  return (
    <div
      onClick={onFlip}
      data-testid="flashcard"
      className="relative w-full max-w-2xl mx-auto h-[360px] cursor-pointer perspective"
      style={{ perspective: "1200px" }}
    >
      <motion.div
        className="relative w-full h-full"
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        style={{ transformStyle: "preserve-3d" }}
      >
        <div
          className="absolute inset-0 nb-border rounded-3xl bg-[#FFE066] flex items-center justify-center p-8 nb-shadow-xl text-black"
          style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
        >
          <div className="text-center">
            <div className="label-caps mb-4">Question</div>
            <div className="font-display text-2xl sm:text-3xl font-extrabold">{card.front}</div>
            <div className="mt-6 text-xs font-bold opacity-60">Tap to flip</div>
          </div>
        </div>
        <div
          className="absolute inset-0 nb-border rounded-3xl bg-[#9DFFB0] flex items-center justify-center p-8 nb-shadow-xl text-black"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          <div className="text-center">
            <div className="label-caps mb-4">Answer</div>
            <div className="font-display text-2xl sm:text-3xl font-extrabold">{card.back}</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function FlashcardsTab({ subject }) {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [mode, setMode] = useState("list"); // list | study
  const [count, setCount] = useState(12);
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [stats, setStats] = useState({ right: 0, wrong: 0 });

  const load = async () => {
    setLoading(true);
    try { setCards(await listFlashcards(subject.id)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [subject.id]);

  const studyDeck = useMemo(() => {
    const now = new Date();
    const due = cards.filter((c) => !c.next_review || new Date(c.next_review) <= now);
    return due.length > 0 ? due : cards;
  }, [cards]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await genFlashcards(subject.id, count);
      toast.success(`Generated ${res.created} flashcards`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const startStudy = () => {
    if (cards.length === 0) return toast.error("No flashcards yet");
    setMode("study");
    setIdx(0);
    setFlipped(false);
    setStats({ right: 0, wrong: 0 });
  };

  const review = async (correct) => {
    const c = studyDeck[idx];
    if (!c) return;
    try { await reviewFlashcard(c.id, correct); } catch (_) { /* ignore */ }
    setStats((s) => ({ right: s.right + (correct ? 1 : 0), wrong: s.wrong + (correct ? 0 : 1) }));
    if (idx + 1 >= studyDeck.length) {
      toast.success("Session complete!");
      setMode("list");
      load();
    } else {
      setIdx((i) => i + 1);
      setFlipped(false);
    }
  };

  const shuffle = () => {
    setCards((c) => [...c].sort(() => Math.random() - 0.5));
    setIdx(0); setFlipped(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this flashcard?")) return;
    await deleteFlashcard(id);
    setCards((c) => c.filter((x) => x.id !== id));
  };

  if (mode === "study" && studyDeck.length > 0) {
    const c = studyDeck[idx];
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="font-bold">
            Card <span className="text-[#7c3aed] dark:text-[#B794F4]">{idx + 1}</span> / {studyDeck.length}
          </div>
          <div className="flex gap-2">
            <span className="nb-border rounded-lg px-3 py-1 bg-[#9DFFB0] text-black font-bold text-sm">✓ {stats.right}</span>
            <span className="nb-border rounded-lg px-3 py-1 bg-[#FFB38E] text-black font-bold text-sm">✗ {stats.wrong}</span>
            <button data-testid="study-exit" onClick={() => setMode("list")} className="nb-button px-3 py-1 text-sm">Exit</button>
          </div>
        </div>
        <FlashcardView card={c} flipped={flipped} onFlip={() => setFlipped((f) => !f)} />
        <div className="flex justify-center gap-3">
          <button data-testid="study-wrong" onClick={() => review(false)} className="nb-button px-6 py-3 bg-[#FFB38E] dark:bg-[#F6AD55] flex items-center gap-2">
            <X className="w-5 h-5" strokeWidth={3} /> Wrong
          </button>
          <button data-testid="study-right" onClick={() => review(true)} className="nb-button px-6 py-3 bg-[#9DFFB0] dark:bg-[#68D391] flex items-center gap-2">
            <Check className="w-5 h-5" strokeWidth={3} /> Got it
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="nb-card p-5 flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <label className="label-caps">Cards:</label>
          <input
            type="number"
            min={4}
            max={30}
            value={count}
            onChange={(e) => setCount(parseInt(e.target.value || 4))}
            className="nb-input w-24"
            data-testid="flashcard-count"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="generate-flashcards-btn"
            onClick={generate}
            disabled={generating}
            className="nb-button px-4 py-2 bg-[#D4C4FB] dark:bg-[#B794F4] flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" strokeWidth={3} /> {generating ? "Generating…" : "Generate"}
          </button>
          {cards.length > 0 && (
            <>
              <button onClick={shuffle} className="nb-button px-3 py-2 flex items-center gap-2"><Shuffle className="w-4 h-4" /></button>
              <button data-testid="start-study" onClick={startStudy} className="nb-button px-4 py-2 bg-[#FFE066] dark:bg-[#F6E05E]">
                Start Study Mode
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="font-bold text-zinc-500">Loading…</div>
      ) : cards.length === 0 ? (
        <div className="nb-card p-8 text-center">
          <div className="font-display text-2xl font-extrabold mb-1">No flashcards yet</div>
          <p className="text-zinc-600 dark:text-zinc-400">Generate from your notes above.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((c) => (
            <div key={c.id} data-testid={`flashcard-${c.id}`} className="nb-card p-5">
              <div className="label-caps mb-1">{c.type === "term" ? "Term" : "Q"}</div>
              <div className="font-display text-lg font-extrabold mb-3 line-clamp-3">{c.front}</div>
              <div className="label-caps mb-1">{c.type === "term" ? "Definition" : "A"}</div>
              <div className="font-medium text-sm line-clamp-4">{c.back}</div>
              <div className="flex items-center justify-between mt-3 text-xs font-bold text-zinc-500">
                <span>✓ {c.times_correct || 0} · ✗ {c.times_wrong || 0}</span>
                <button onClick={() => remove(c.id)} className="nb-border rounded-lg p-1.5 hover:bg-red-100 dark:hover:bg-red-900">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
